import React from 'react';
import { Download } from 'lucide-react';
import { CARD_BRANDS, getRateOffers, QuoteRequest, RateOffer } from '../services/quoteService';
import { downloadRatePdf } from '../utils/ratePdf';
import { logger } from '../utils/logger';

const statusLabel = (status?: RateOffer['status']) => {
  switch (status) {
    case 'accepted':
      return 'Accepted';
    case 'declined':
      return 'Declined';
    case 'revision_requested':
      return 'New rate requested';
    case 'superseded':
      return 'Replaced';
    case 'offered':
      return 'Current offer';
    default:
      return 'On file';
  }
};

const RateHistory: React.FC<{ quote: QuoteRequest; pastOnly?: boolean }> = ({ quote, pastOnly }) => {
  const allOffers = getRateOffers(quote);
  const offers = pastOnly && allOffers.length > 1 ? allOffers.slice(0, -1) : pastOnly ? [] : allOffers;
  if (offers.length === 0) return null;

  const handleDownload = async (offer: RateOffer, version: number) => {
    try {
      await downloadRatePdf(quote, offer, version);
    } catch (error) {
      logger.error('Error downloading rate PDF:', error);
    }
  };

  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm font-semibold text-gray-900">Rate history</p>
      {offers.map((offer, index) => {
        const version = pastOnly ? index + 1 : index + 1;
        const isLatest = !pastOnly && index === offers.length - 1;
        return (
          <div key={`${quote.id}-rate-${version}`} className="rounded-lg border border-gray-200 p-3">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {offer.type === 'ic_plus' ? 'I/C plus' : 'Fixed'} · Version {version}
                  {isLatest ? ' (latest)' : ''}
                </p>
                <p className="text-xs text-gray-500">
                  {statusLabel(offer.status)}
                  {offer.offeredAt ? ` · ${offer.offeredAt.toLocaleDateString()}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleDownload(offer, version)}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
              >
                <Download className="h-3.5 w-3.5" />
                PDF
              </button>
            </div>
            <div className="space-y-1">
              {CARD_BRANDS.map((brand) => (
                <div key={brand.id} className="flex items-center justify-between text-xs text-gray-700">
                  <span>{brand.label}</span>
                  <span>
                    {offer.cards?.[brand.id]?.percent ?? 0}% + $
                    {(offer.cards?.[brand.id]?.fee ?? 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RateHistory;
